/**
 * IconPicker Component
 * Displays available sprite icons in a grid for selection.
 * Used for asset type and subtype icon configuration.
 * 
 * Renders icon previews using the shape language from assetShapeLanguage.js:
 * - Circle shape for gateway assets
 * - Squircle shape for beacon-enabled assets
 */

import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  Grid,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';

import SpriteIcon from './SpriteIcon';
import { useAppSelector } from '../../store/hooks';
import { ASSET_SHAPE_BORDER_RADIUS_MUI } from '../../utils/assetShapeLanguage';

/**
 * IconPicker - A modal dialog for selecting icons from the sprite atlas
 * 
 * @param {boolean} open - Whether the dialog is open
 * @param {function} onClose - Called when dialog closes without selection
 * @param {function} onSelect - Called with the selected icon name
 * @param {string} currentIcon - Currently selected icon name (for highlighting)
 * @param {string} previewColor - Color to use for the preview background
 * @param {string} shape - 'circle' or 'squircle' for preview container shape
 */
const IconPicker = ({
  open,
  onClose,
  onSelect,
  currentIcon = '',
  previewColor = '#EC4F36',
  shape = 'squircle',
}) => {
  const { iconData, iconsLoaded } = useAppSelector((state) => state.ui);
  const [searchQuery, setSearchQuery] = useState('');

  // Extract available icon names from the sprite atlas
  const availableIcons = useMemo(() => {
    if (!iconsLoaded || !iconData) return [];
    
    // Get all icon names, normalize by removing extensions
    const icons = Object.keys(iconData)
      .map((name) => {
        // Remove .png or .svg extension
        const baseName = name.replace(/\.(png|svg)$/i, '');
        return { key: name, baseName };
      })
      // Filter out marker/path/start variants for cleaner selection
      .filter(({ baseName }) => {
        const lower = baseName.toLowerCase();
        // Keep equipment icons but filter area markers
        return !lower.includes('_marker') || lower.includes('mat_marker') || lower.includes('equipment_marker');
      })
      // Remove duplicates (same base name with different extensions)
      .reduce((acc, { key, baseName }) => {
        if (!acc.find(i => i.baseName === baseName)) {
          acc.push({ key, baseName });
        }
        return acc;
      }, [])
      // Sort alphabetically
      .sort((a, b) => a.baseName.localeCompare(b.baseName));
    
    return icons;
  }, [iconData, iconsLoaded]);

  // Filter icons by search query
  const filteredIcons = useMemo(() => {
    if (!searchQuery.trim()) return availableIcons;
    const query = searchQuery.toLowerCase();
    return availableIcons.filter(({ baseName }) =>
      baseName.toLowerCase().includes(query)
    );
  }, [availableIcons, searchQuery]);

  const handleSelect = (iconName) => {
    onSelect(iconName);
    onClose();
  };

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  const borderRadius = ASSET_SHAPE_BORDER_RADIUS_MUI[shape] || 1;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '60vh', maxHeight: '80vh' },
      }}
    >
      <DialogTitle>Select Icon</DialogTitle>
      <DialogContent>
        {/* Search Field */}
        <TextField
          fullWidth
          placeholder="Search icons..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleClearSearch}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {/* Icons Grid */}
        {!iconsLoaded ? (
          <Typography color="text.secondary">Loading icons...</Typography>
        ) : filteredIcons.length === 0 ? (
          <Typography color="text.secondary">
            No icons found matching &quot;{searchQuery}&quot;
          </Typography>
        ) : (
          <Grid container spacing={1}>
            {filteredIcons.map(({ key, baseName }) => {
              const isSelected = currentIcon === baseName || currentIcon === key;
              return (
                <Grid item key={baseName}>
                  <Tooltip title={baseName} placement="top">
                    <Box
                      onClick={() => handleSelect(baseName)}
                      sx={{
                        width: 56,
                        height: 56,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: borderRadius,
                        backgroundColor: isSelected ? previewColor : 'background.paper',
                        border: isSelected
                          ? `2px solid ${previewColor}`
                          : '1px solid',
                        borderColor: isSelected ? previewColor : 'divider',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                        '&:hover': {
                          backgroundColor: isSelected
                            ? previewColor
                            : 'action.hover',
                          transform: 'scale(1.05)',
                        },
                      }}
                    >
                      <SpriteIcon iconName={baseName} size={32} />
                    </Box>
                  </Tooltip>
                </Grid>
              );
            })}
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {currentIcon && (
          <Button
            onClick={() => handleSelect('')}
            color="error"
          >
            Clear Selection
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

/**
 * IconPickerButton - A button that shows current icon and opens picker on click
 */
export const IconPickerButton = ({
  iconName,
  color = '#EC4F36',
  shape = 'squircle',
  onSelect,
  size = 44,
  label = 'Select Icon',
}) => {
  const [pickerOpen, setPickerOpen] = useState(false);

  const borderRadius = ASSET_SHAPE_BORDER_RADIUS_MUI[shape] || 1;

  return (
    <>
      <Box
        onClick={() => setPickerOpen(true)}
        sx={{
          width: size,
          height: size,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: borderRadius,
          backgroundColor: color,
          cursor: 'pointer',
          transition: 'all 0.15s ease',
          border: '2px solid transparent',
          '&:hover': {
            borderColor: 'primary.main',
            transform: 'scale(1.05)',
          },
        }}
        title={label}
      >
        {iconName ? (
          <SpriteIcon iconName={iconName} size={size - 12} />
        ) : (
          <Typography variant="caption" sx={{ color: 'white', fontSize: 10 }}>
            +
          </Typography>
        )}
      </Box>
      <IconPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={onSelect}
        currentIcon={iconName}
        previewColor={color}
        shape={shape}
      />
    </>
  );
};

export default IconPicker;
